import { useState } from 'react'
import * as yup from 'yup'
import {
  validateFunction,
  findError,
  deleteError,
} from '@/utils/form-errors/validate-form'
import { ErrorWrapper } from '@/components/forms/validation-errors/styled'
import InputComponent from '../input'
import { Multiselect } from '@components'
import {
  Stepper,
  Pagination,
  Title,
  Divider,
  Label,
  Block,
  Item,
  Button,
  Arrow,
} from '../styled'
import { geographicalData } from '@/mocks'
import { TitleTooltip } from '../tooltip-content'
import { StepOneParams } from './types'
import CustomMultiSelect from '../custom-multi-select'
import { components } from 'react-select'
import makeAnimated from 'react-select/animated'

const FirstStep = ({
  onClick,
  setFirstStepState,
  previousState,
}: StepOneParams) => {
  const [title, setTitle] = useState(previousState?.title || '')
  const [geo, setGeo] = useState(() => {
    if (previousState?.geo && Array.isArray(previousState.geo)) {
      return geographicalData.filter((option) =>
        previousState.geo.includes(option.label),
      )
    }
    return []
  })
  const [errors, setErrors] = useState<yup.ValidationError[]>([])

  const validateSchema = {
    title: yup
      .string()
      .required('Title is required.')
      .min(30, "Title can't be shorter than 30 characters"),
  }

  type stateType = {
    title: string
  }

  const internalValidateFunction = async (
    value: stateType | stateType[keyof stateType],
    lookup?: keyof stateType,
  ): Promise<boolean> => {
    const newErrors = await validateFunction<stateType>(
      value,
      validateSchema,
      lookup,
    )
    setErrors(newErrors)
    return newErrors.length === 0
  }

  const internalDeleteError = (identifier: string) => {
    deleteError(identifier, errors, setErrors)
  }

  const internalFindError = (identifier: string) => {
    const error = findError(identifier, errors)
    return !!error && <ErrorWrapper>{error.message}</ErrorWrapper>
  }

  const firstStepValues = {
    title,
    geo: geo.map(({ label }) => label),
  }

  const Option = (props: any) => {
    return (
      <div>
        <components.Option {...props}>
          <input
            type="checkbox"
            checked={props.isSelected}
            onChange={() => null}
          />{' '}
          <label>{props.label}</label>
        </components.Option>
      </div>
    )
  }

  const MultiValue = (props: any) => (
    <components.MultiValue {...props}>
      <span>{props.data.label}</span>
    </components.MultiValue>
  )

  const animatedComponents = makeAnimated()

  const handleChange = (selected: []) => {
    setGeo(selected)
  }

  return (
    <Stepper>
      <Block>
        <Pagination>Step 1/3</Pagination>
        <Title>Data Needs</Title>
        <Divider />
        <Item
          onFocus={() => {
            internalDeleteError('title')
          }}
          onBlur={(e: any) => internalValidateFunction(title, 'title')}
        >
          <InputComponent
            title="What data are you looking for?"
            minCharacters={30}
            value={title}
            getValue={(e: any) => setTitle(e.target.value)}
          >
            <TitleTooltip />
          </InputComponent>
          {internalFindError('title')}
        </Item>
      </Block>
      <Block>
        <Item>
          <Title>Geo-Coverage</Title>
          <Divider />
          <Label>Which countries should be covered?</Label>
          <CustomMultiSelect
            options={geographicalData}
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{ Option, MultiValue, animatedComponents }}
            onChange={handleChange}
            allowSelectAll={true}
            value={geo}
          />
        </Item>
      </Block>
      <Button
        onClick={async (e) => {
          e.preventDefault
          const isValid = await internalValidateFunction({
            title,
          })
          if (isValid) {
            onClick(e)
            setFirstStepState(firstStepValues)
          }
        }}
      >
        Continue to next step
        <Arrow />
      </Button>
      {errors.length > 0 && (
        <ErrorWrapper>You must resolve the errors to continue</ErrorWrapper>
      )}
    </Stepper>
  )
}

export default FirstStep
